import { create } from 'zustand';

export interface PendingApproval {
    approvalId: string;
    toolName: string;
    toolArgs: unknown;
    message?: string;
    sessionId: string;
    timestamp: number;
    status: 'pending' | 'approved' | 'rejected';
    metadata?: Record<string, unknown>; // For elicitation schema and other approval-specific data
}

interface ApprovalStore {
    // State
    approvals: Map<string, PendingApproval>;

    // Actions
    addApproval: (approval: Omit<PendingApproval, 'timestamp' | 'status'>) => void;
    removeApproval: (approvalId: string) => void;
    updateApprovalStatus: (approvalId: string, status: 'approved' | 'rejected') => void;
    getApprovalsBySession: (sessionId: string) => PendingApproval[];
    getPendingCount: () => number;
    clearApprovals: () => void;
}

export const useApprovalStore = create<ApprovalStore>((set, get) => ({
    // Initial state
    approvals: new Map(),

    // Actions
    addApproval: (approval) => {
        set((state) => {
            const newApprovals = new Map(state.approvals);
            newApprovals.set(approval.approvalId, {
                ...approval,
                timestamp: Date.now(),
                status: 'pending',
            });
            return { approvals: newApprovals };
        });
    },

    removeApproval: (approvalId) => {
        set((state) => {
            const newApprovals = new Map(state.approvals);
            newApprovals.delete(approvalId);
            return { approvals: newApprovals };
        });
    },

    updateApprovalStatus: (approvalId, status) => {
        set((state) => {
            const newApprovals = new Map(state.approvals);
            const approval = newApprovals.get(approvalId);
            if (approval) {
                newApprovals.set(approvalId, { ...approval, status });
            }
            return { approvals: newApprovals };
        });
    },

    getApprovalsBySession: (sessionId) => {
        const { approvals } = get();
        return Array.from(approvals.values()).filter(
            (approval) => approval.sessionId === sessionId
        );
    },

    getPendingCount: () => {
        const { approvals } = get();
        return Array.from(approvals.values()).filter(
            (approval) => approval.status === 'pending'
        ).length;
    },

    clearApprovals: () => {
        set({ approvals: new Map() });
    },
}));
